import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { BookOpen, ChevronRight, Clock, CheckCircle, AlertTriangle, Target, ExternalLink, ChevronDown, ChevronUp, X, MessageSquare, Settings, Users, Award, Lightbulb, List } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import ExportButton from '../components/ExportButton';
import ChatPanel from '../components/ChatPanel';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// ── Referential alignment modal ─────────────────────────────────────────────
function AlignmentModal({ courseId, currentAlignments, onClose, onSaved }) {
  const [referentials, setReferentials] = useState([]);
  const [selected, setSelected] = useState(currentAlignments || []);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/referentials`)
      .then(r => setReferentials(r.data.referentials || []))
      .catch(() => {});
  }, []);

  const toggle = (refName) => {
    setSelected(prev =>
      prev.includes(refName) ? prev.filter(n => n !== refName) : [...prev, refName]
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/courses/${courseId}/alignments`, { alignments: selected }, { withCredentials: true });
      onSaved(selected);
      onClose();
    } catch {
      /* ignore */
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-[#1a1927] border border-white/10 rounded-2xl p-6 max-w-md w-full max-h-[80vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
        data-testid="alignment-modal"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-bold text-base">Aligner sur un référentiel</h3>
          <button onClick={onClose} className="text-[#a0a0ab] hover:text-white"><X size={16} /></button>
        </div>
        <p className="text-[#a0a0ab] text-xs mb-4">
          Associez ce cours aux référentiels officiels pour faciliter les financements OPCO et la certification Qualiopi.
        </p>
        <div className="space-y-2 mb-5">
          {referentials.map(ref => (
            <label
              key={ref.name}
              className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                selected.includes(ref.name)
                  ? 'bg-[#ff6b6b]/10 border-[#ff6b6b]/40'
                  : 'bg-white/5 border-white/10 hover:border-white/20'
              }`}
            >
              <input
                type="checkbox"
                checked={selected.includes(ref.name)}
                onChange={() => toggle(ref.name)}
                className="hidden"
              />
              <div className={`w-4 h-4 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                selected.includes(ref.name) ? 'bg-[#ff6b6b] border-[#ff6b6b]' : 'border-white/30'
              }`}>
                {selected.includes(ref.name) && <CheckCircle size={10} className="text-white" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-medium">{ref.name}</p>
                <p className="text-[#a0a0ab] text-xs truncate">{ref.organization}</p>
              </div>
            </label>
          ))}
        </div>
        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-white/10 text-[#a0a0ab] text-sm hover:bg-white/5">
            Annuler
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            data-testid="save-alignments-btn"
            className="flex-1 py-2.5 rounded-xl bg-[#ff6b6b] text-white text-sm font-bold hover:bg-[#ff5555] disabled:opacity-60"
          >
            {saving ? 'Enregistrement...' : 'Sauvegarder'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Targeted Referentials Disclaimer ─────────────────────────────────────────
function TargetedReferentials({ referentials }) {
  if (!referentials || referentials.length === 0) return null;
  return (
    <div className="bg-[#20c997]/8 border border-[#20c997]/25 rounded-2xl p-4" data-testid="targeted-referentials-banner">
      <div className="flex items-start gap-3">
        <Target size={15} className="text-[#20c997] mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-[#20c997] font-semibold text-sm">Compétences ciblées lors de la création</p>
          <div className="flex flex-wrap gap-1.5 mt-1.5">
            {referentials.map(name => (
              <span key={name} className="inline-flex items-center gap-1 text-xs px-2.5 py-0.5 rounded-full bg-[#20c997]/10 border border-[#20c997]/20 text-[#20c997] font-medium">
                {name}
              </span>
            ))}
          </div>
          <p className="text-[#a0a0ab] text-xs mt-2 leading-relaxed">
            Ce cours a été généré par l'IA pour préparer aux référentiels mentionnés. Cet alignement est indicatif et ne constitue pas une certification officielle. Pour une certification, contactez un organisme habilité.
          </p>
        </div>
      </div>
    </div>
  );
}

// ── Universal Disclaimer Banner ──────────────────────────────────────────────
function UniversalDisclaimer({ alignments }) {
  const [open, setOpen] = useState(false);
  if (!alignments || alignments.length === 0) return null;

  return (
    <div
      className="bg-[#ffc107]/8 border border-[#ffc107]/30 rounded-2xl p-4"
      data-testid="universal-disclaimer"
    >
      <div className="flex items-start gap-3">
        <AlertTriangle size={16} className="text-[#ffc107] mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <p className="text-[#ffc107] font-semibold text-sm">Avertissement universel — Alignement référentiel</p>
            <button onClick={() => setOpen(v => !v)} className="text-[#ffc107]/70 hover:text-[#ffc107] ml-2">
              {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>
          </div>
          <p className="text-[#e8c84a] text-xs mt-1">
            Ce cours est aligné sur : <span className="font-semibold">{alignments.join(', ')}</span>
          </p>
          {open && (
            <p className="text-[#a0a0ab] text-xs mt-2 leading-relaxed">
              Ce cours a été créé sur Kurso à l'aide de l'intelligence artificielle et est aligné sur les référentiels officiels mentionnés ci-dessus. Cet alignement est indicatif et ne constitue pas une certification officielle. Les compétences couvertes sont déterminées automatiquement. Pour une certification officielle, veuillez contacter un organisme habilité.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Competency Badges ─────────────────────────────────────────────────────────
function CompetencyBadges({ alignments }) {
  if (!alignments || alignments.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-2" data-testid="competency-badges">
      {alignments.map(name => (
        <span
          key={name}
          className="inline-flex items-center gap-1.5 text-xs px-3 py-1 rounded-full bg-[#ff6b6b]/10 border border-[#ff6b6b]/20 text-[#ff6b6b] font-medium"
        >
          <Target size={10} />
          {name}
        </span>
      ))}
    </div>
  );
}

// ── Teaser Section (généré par Claude, fiche Qualiopi) ────────────────────────
function TeaserSection({ teaser }) {
  if (!teaser || Object.keys(teaser).length === 0) return null;

  return (
    <div className="bg-[#16161e] border border-[#ff6b6b]/20 rounded-2xl p-5 space-y-4 animate-fade-up" data-testid="teaser-section">
      <div className="flex items-center gap-2">
        <div className="w-6 h-6 rounded-lg bg-[#ff6b6b]/10 flex items-center justify-center">
          <Award size={12} className="text-[#ff6b6b]" />
        </div>
        <span className="text-[#ff6b6b] text-xs font-bold uppercase tracking-wider">Présentation de la formation</span>
      </div>

      {teaser.hook && (
        <p className="text-white text-base font-semibold leading-relaxed italic">"{teaser.hook}"</p>
      )}
      {teaser.description && (
        <p className="text-[#e8e8e8] text-sm leading-relaxed">{teaser.description}</p>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {teaser.target_audience && (
          <div className="bg-white/5 rounded-xl p-3">
            <div className="flex items-center gap-1.5 mb-2">
              <Users size={11} className="text-[#4d9eff]" />
              <p className="text-[10px] uppercase tracking-wider font-bold text-[#4d9eff]">Public visé</p>
            </div>
            <p className="text-[#e8e8e8] text-xs leading-relaxed">{teaser.target_audience}</p>
          </div>
        )}

        {teaser.prerequisites?.length > 0 && (
          <div className="bg-white/5 rounded-xl p-3">
            <div className="flex items-center gap-1.5 mb-2">
              <Target size={11} className="text-[#ffd93d]" />
              <p className="text-[10px] uppercase tracking-wider font-bold text-[#ffd93d]">Prérequis</p>
            </div>
            <ul className="space-y-1">
              {teaser.prerequisites.map((p, i) => (
                <li key={i} className="text-[#e8e8e8] text-xs flex items-start gap-1.5">
                  <span className="text-[#ffd93d] mt-0.5 shrink-0">•</span> {p}
                </li>
              ))}
            </ul>
          </div>
        )}

        {teaser.learning_outcomes?.length > 0 && (
          <div className="bg-white/5 rounded-xl p-3 sm:col-span-2">
            <div className="flex items-center gap-1.5 mb-2">
              <Lightbulb size={11} className="text-[#20c997]" />
              <p className="text-[10px] uppercase tracking-wider font-bold text-[#20c997]">Objectifs pédagogiques</p>
            </div>
            <ul className="space-y-1.5">
              {teaser.learning_outcomes.map((o, i) => (
                <li key={i} className="text-[#e8e8e8] text-xs flex items-start gap-1.5">
                  <CheckCircle size={11} className="text-[#20c997] mt-0.5 shrink-0" /> {o}
                </li>
              ))}
            </ul>
          </div>
        )}

        {(teaser.duration_details || teaser.qualiopi_action_type) && (
          <div className="bg-white/5 rounded-xl p-3 sm:col-span-2">
            <div className="flex items-center gap-1.5 mb-2">
              <List size={11} className="text-[#a78bfa]" />
              <p className="text-[10px] uppercase tracking-wider font-bold text-[#a78bfa]">Modalités</p>
            </div>
            <div className="flex flex-wrap gap-3">
              {teaser.qualiopi_action_type && (
                <span className="text-xs bg-[#a78bfa]/10 text-[#a78bfa] border border-[#a78bfa]/20 px-2.5 py-1 rounded-full">
                  {teaser.qualiopi_action_type}
                </span>
              )}
              {teaser.duration_details && (
                <p className="text-[#94a3b8] text-xs self-center">{teaser.duration_details}</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Qualiopi Section ─────────────────────────────────────────────────────────
function QualiopiSection({ qualiopi, organization }) {
  const [open, setOpen] = useState(false);
  if (!qualiopi) return null;

  const fields = [
    { key: 'objectifs',                   label: 'Objectifs pédagogiques' },
    { key: 'public_vise',                 label: 'Public visé' },
    { key: 'prerequis',                   label: 'Prérequis' },
    { key: 'duree',                       label: 'Durée' },
    { key: 'modalites',                   label: 'Modalités' },
    { key: 'dates_ou_acces_continu',      label: 'Accès / Dates' },
    { key: 'delais_acces',                label: 'Délais d\'accès' },
    { key: 'tarif',                       label: 'Tarif' },
    { key: 'modalites_evaluation',        label: 'Modalités d\'évaluation' },
    { key: 'moyens_pedagogiques',         label: 'Moyens pédagogiques' },
    { key: 'formateur_referent',          label: 'Formateur / Référent' },
    { key: 'type_reconnaissance',         label: 'Reconnaissance visée' },
    { key: 'modalites_financement',       label: 'Financement mobilisable' },
    { key: 'accessibilite_handicap',      label: 'Accessibilité handicap' },
    { key: 'referent_handicap_contact',   label: 'Contact référent handicap' },
    { key: 'contact_referent_pedagogique', label: 'Référent pédagogique' },
  ];

  const orgColors = organization === 'sanctuaire'
    ? { border: 'border-[#F2B705]/30', bg: 'bg-[#0C1940]', accent: 'text-[#F2B705]', badge: 'bg-[#F2B705]/10 text-[#F2B705] border-[#F2B705]/30' }
    : organization === 'numericli'
    ? { border: 'border-[#20c997]/30', bg: 'bg-[#0a1a14]', accent: 'text-[#20c997]', badge: 'bg-[#20c997]/10 text-[#20c997] border-[#20c997]/30' }
    : { border: 'border-white/10', bg: 'bg-[#16161e]', accent: 'text-[#ff6b6b]', badge: 'bg-[#ff6b6b]/10 text-[#ff6b6b] border-[#ff6b6b]/30' };

  return (
    <div className={`${orgColors.bg} border ${orgColors.border} rounded-2xl p-5`} data-testid="qualiopi-section">
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between gap-3"
      >
        <div className="flex items-center gap-2">
          <div className={`w-6 h-6 rounded-lg flex items-center justify-center ${orgColors.badge}`}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
            </svg>
          </div>
          <span className={`font-semibold text-sm ${orgColors.accent}`}>Fiche programme Qualiopi</span>
          {organization && organization !== 'standard' && (
            <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium uppercase ${orgColors.badge}`}>
              {organization === 'sanctuaire' ? 'Sanctuaire' : 'Numericli'}
            </span>
          )}
        </div>
        {open ? <ChevronUp size={14} className="text-[#64748b]" /> : <ChevronDown size={14} className="text-[#64748b]" />}
      </button>

      {open && (
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {fields.map(({ key, label }) => {
            const value = qualiopi[key];
            if (!value) return null;
            return (
              <div key={key} className="bg-white/5 rounded-xl p-3">
                <p className={`text-[10px] uppercase tracking-wider font-bold mb-1 ${orgColors.accent}`}>{label}</p>
                <p className="text-[#e8e8e8] text-xs leading-relaxed">{value}</p>
              </div>
            );
          })}
          {(qualiopi.taux_satisfaction != null || qualiopi.taux_reussite != null) && (
            <div className="sm:col-span-2 flex gap-3">
              {qualiopi.taux_satisfaction != null && (
                <div className="flex-1 bg-white/5 rounded-xl p-3 text-center">
                  <p className={`text-2xl font-bold ${orgColors.accent}`}>{qualiopi.taux_satisfaction}%</p>
                  <p className="text-[#94a3b8] text-xs mt-0.5">Satisfaction</p>
                </div>
              )}
              {qualiopi.taux_reussite != null && (
                <div className="flex-1 bg-white/5 rounded-xl p-3 text-center">
                  <p className={`text-2xl font-bold ${orgColors.accent}`}>{qualiopi.taux_reussite}%</p>
                  <p className="text-[#94a3b8] text-xs mt-0.5">Réussite</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// ── Main Component ─────────────────────────────────────────────────────────────
export default function CourseOverview() {
  const { courseId } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [alignments, setAlignments] = useState([]);
  const [chatOpen, setChatOpen] = useState(false);

  const loadProgress = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/courses/${courseId}/progress`, { withCredentials: true });
      setProgress(r.data);
    } catch { /* ignore */ }
  }, [courseId]);

  useEffect(() => {
    const load = async () => {
      try {
        const [courseRes, progressRes] = await Promise.all([
          axios.get(`${API}/courses/${courseId}`, { withCredentials: true }),
          axios.get(`${API}/courses/${courseId}/progress`, { withCredentials: true }),
        ]);
        setCourse(courseRes.data);
        setProgress(progressRes.data);
        setAlignments(courseRes.data.alignments || []);
      } catch {
        navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [courseId, navigate]);

  // Refresh progress when user returns from a lesson
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') loadProgress();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [loadProgress]);

  if (loading) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto px-4 pt-6 space-y-4">
          {[1, 2, 3, 4, 5].map(i => <div key={i} className="animate-shimmer h-24 rounded-2xl" />)}
        </div>
      </Layout>
    );
  }

  if (!course) return null;

  const completedLessons = progress?.completed_lessons || [];
  const modules = course.modules || [];
  const completedCount = modules.filter(m =>
    completedLessons.includes(m.lesson?.id) || completedLessons.includes(m.id)
  ).length;
  const progressPct = modules.length > 0 ? (completedCount / modules.length) * 100 : 0;
  const allDone = completedCount === modules.length && modules.length > 0;

  const MODULE_COLORS = ['#ff6b6b', '#ffc107', '#20c997', '#3b82f6', '#a855f7'];

  return (
    <Layout>
      <div className="max-w-2xl mx-auto px-4 pt-6 pb-4 space-y-6">
        {/* Header */}
        <div className="animate-fade-up">
          <div className="flex items-center gap-2">
            <button onClick={() => navigate('/library')} className="text-[#e8e8e8] text-sm hover:text-white flex items-center gap-1 transition-colors" data-testid="back-to-dashboard">
              {t('course.backToCourses')}
            </button>
            <div className="flex items-center gap-2 ml-auto">
              <button
                onClick={() => navigate(`/course/${courseId}/edit`)}
                data-testid="edit-course-btn"
                className="flex items-center gap-2 bg-[#16161e] border border-[#2a2935] text-[#94a3b8] px-3.5 py-2 rounded-xl text-sm font-medium hover:border-[#3a3945] hover:text-white transition-all"
              >
                <Settings size={14} /> Éditer
              </button>
              <button
                onClick={() => setChatOpen(true)}
                data-testid="open-chat-btn"
                className="flex items-center gap-2 bg-[#16161e] border border-[#20c997]/30 text-[#20c997] px-3.5 py-2 rounded-xl text-sm font-medium hover:bg-[#20c997]/10 transition-all"
              >
                <MessageSquare size={14} /> Chat IA
              </button>
            </div>
          </div>
          <div className="kurso-card p-6">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 bg-[#ff6b6b]/10 rounded-2xl flex items-center justify-center shrink-0">
                <BookOpen size={24} className="text-[#ff6b6b]" />
              </div>
              <div className="flex-1 min-w-0">
                <h1 className="font-heading text-xl font-bold text-white leading-tight">{course.title}</h1>
                <p className="text-[#e8e8e8] text-sm mt-1 leading-relaxed">{course.description}</p>
                <div className="flex items-center gap-3 mt-3">
                  <span className="flex items-center gap-1 text-xs text-[#e8e8e8]">
                    <Clock size={12} /> {t('course.estimatedHours', { n: course.estimated_hours })}
                  </span>
                  <span className="text-xs text-[#e8e8e8]">
                    {modules.length} module{modules.length !== 1 ? 's' : ''}
                  </span>
                </div>
                {course.quality_report && (
                  <span className="inline-flex items-center gap-1.5 mt-3 text-[11px] text-[#20c997] bg-[#20c997]/10 border border-[#20c997]/20 rounded-full px-2.5 py-1" data-testid="quality-verified-badge">
                    <CheckCircle size={11} />
                    {t('course.qualityVerified', { modules: course.quality_report.modules, videos: course.quality_report.intro_videos, quizzes: course.quality_report.quizzes })}
                  </span>
                )}
              </div>
            </div>

            {/* Progress */}
            <div className="mt-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-[#e8e8e8]" data-testid="progress-label">
                  {t('course.moduleProgressLabel', { done: completedCount, total: modules.length })}
                </span>
                <span className="text-sm text-[#ff6b6b] font-medium" data-testid="progress-pct">
                  {Math.round(progressPct)}%
                </span>
              </div>
              <div className="progress-bar-bg h-2">
                <div className="progress-bar-fill h-2 transition-all duration-500" style={{ width: `${progressPct}%` }} />
              </div>
            </div>

            {/* Competency badges + Export */}
            <div className="mt-3 flex items-center justify-end gap-2">
              <ExportButton courseId={courseId} courseTitle={course.title} />
            </div>
          </div>
        </div>

        {/* Targeted Referentials + Legacy Alignments */}
        <TargetedReferentials referentials={course.targeted_referentials} />
        <UniversalDisclaimer alignments={alignments} />

        {/* Teaser de la formation (généré par Claude) */}
        <TeaserSection teaser={course.teaser} />

        {/* Qualiopi Section */}
        <QualiopiSection qualiopi={course.qualiopi} organization={course.organization} />

        {/* Modules */}
        <div className="space-y-3 animate-fade-up" style={{ animationDelay: '0.1s' }}>
          <h2 className="font-heading text-lg font-semibold text-white">{t('course.learningPath')}</h2>
          {modules.length === 0 ? (
            <p className="text-[#e8e8e8] text-sm">{t('course.noModules')}</p>
          ) : (
            modules.map((module, i) => {
              const lessonId = module.lesson?.id || module.id;
              const completed = completedLessons.includes(lessonId) || completedLessons.includes(module.id);
              const quizScore = progress?.quiz_scores?.[lessonId];
              const color = MODULE_COLORS[i % MODULE_COLORS.length];

              return (
                <button
                  key={module.id}
                  onClick={() => navigate(`/course/${courseId}/module/${module.id}`)}
                  className="w-full kurso-card p-4 flex items-center gap-4 text-left hover:-translate-y-0.5 transition-transform duration-200"
                  data-testid={`module-card-${module.id}`}
                >
                  <div
                    className="w-10 h-10 rounded-2xl flex items-center justify-center shrink-0 font-heading font-bold text-sm"
                    style={{ backgroundColor: `${color}15`, color }}
                  >
                    {completed ? <CheckCircle size={16} className="text-[#20c997]" /> : (module.order || i + 1)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium text-sm leading-tight">{module.title}</p>
                    <p className="text-[#e8e8e8] text-xs mt-0.5 truncate">{module.description}</p>
                    {quizScore !== undefined && (
                      <span className="teal-badge mt-1 inline-flex">
                        {t('course.quizScoreLabel', { pct: Math.round(quizScore * 100) })}
                      </span>
                    )}
                  </div>
                  <ChevronRight size={16} className="text-[#e8e8e8] shrink-0" />
                </button>
              );
            })
          )}
        </div>

        {/* Complete button */}
        {allDone && !progress?.completed && (
          <button
            onClick={() => navigate(`/course/${courseId}/completion`)}
            className="btn-primary w-full py-4 text-base"
            data-testid="complete-course-btn"
          >
            {t('course.completeCourseBtn')}
          </button>
        )}
        {progress?.completed && (
          <div className="teal-badge px-4 py-3 rounded-2xl flex items-center gap-2 w-full justify-center" data-testid="course-completed-badge">
            <CheckCircle size={16} /> {t('course.courseCompletedBadge')}
          </div>
        )}
      </div>

      {/* Chat IA panel */}
      <ChatPanel
        isOpen={chatOpen}
        onClose={() => setChatOpen(false)}
        courseId={courseId}
        courseTitle={course?.title}
      />
    </Layout>
  );
}
